import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { usePageSeo } from "@/hooks/usePageSeo";

const TermsOfService = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  usePageSeo({
    title: 'Terms of Service | PropertyTaxHero',
    description: 'Read the PropertyTaxHero terms of service. Understand your rights and responsibilities when using our free property tax appeal tools.',
    path: '/terms',
    noindex: true,
  });

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <div className="container mx-auto px-4 py-8 flex-1">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold text-tax-deepBlue mb-8">Terms of Service</h1>
          
          <p className="text-tax-neutralGray mb-8">
            <strong>Effective Date:</strong> August 15, 2025
          </p>
          
          <div className="prose prose-slate max-w-none text-tax-neutralGray">
            <p className="mb-6">
              Welcome to PropertyTaxHero.com, a service provided by YourWayHome.com LLC ("Company," "we," "us," or "our"). 
              These Terms of Service ("Terms") govern your access to and use of our website, services, and content 
              (collectively, the "Services"). By accessing or using PropertyTaxHero.com, you agree to be bound by 
              these Terms and our Privacy Policy. If you do not agree, you may not use the Services.
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-tax-deepBlue mb-4">1. About the Service</h2>
              <p className="mb-4">
                PropertyTaxHero.com is designed to help homeowners review and prepare appeals of property tax valuations. 
                The Services use publicly available property record data and user-submitted information to generate reports and related content.
              </p>
              <p className="mb-4">
                PropertyTaxHero.com is a service of YourWayHome.com LLC. Your data may also be shared with our wholly owned 
                websites, including MortgageNews.org, in order to provide you with related services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-tax-deepBlue mb-4">2. Information Sharing & Agent Referrals</h2>
              <p className="mb-4">
                We do not sell or share your personal information with third parties, except as described below.
              </p>
              <p className="mb-4">
                If you were referred to the site by a real estate agent whose name and photo are displayed on the website 
                with the label "Courtesy Of," then that referring agent will have access to your name, property details, 
                and your generated report. This is so they can assist you with questions or modifications to the report.
              </p>
              <p className="mb-4">
                We do not collect or store confidential financial data. Other than your email and phone number, all property 
                data we use is publicly available through county and state records.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-tax-deepBlue mb-4">3. Eligibility</h2>
              <p className="mb-4">
                You must be at least 18 years of age to use the Services. By using the Services, you represent that you 
                are legally able to enter into these Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-tax-deepBlue mb-4">4. License and Restrictions</h2>
              <p className="mb-4">
                We grant you a limited, non-exclusive, non-transferable license to access and use the Services for your 
                personal, non-commercial purposes. You agree that you will not:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li className="mb-2">Copy, reproduce, distribute, modify, or create derivative works based on the Services.</li>
                <li className="mb-2">Scrape, crawl, or use any automated means to access the Services or data contained within them.</li>
                <li className="mb-2">Frame, mirror, or rebrand the Services under a different name without our prior written consent.</li>
                <li className="mb-2">Reverse engineer, decompile, or disassemble any part of the Services.</li>
                <li className="mb-2">Access or attempt to access any APIs, systems, or data using keys or credentials not expressly provided to you by us.</li>
                <li className="mb-2">Use the Services in any way that violates applicable law, infringes upon the rights of others, or interferes with the operation of the Services.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-tax-deepBlue mb-4">5. Intellectual Property</h2>
              <p className="mb-4">
                All content, software, features, trademarks, and logos available through the Services are the property 
                of YourWayHome.com LLC or its licensors and are protected by intellectual property laws. You may not use 
                them without our express written permission.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-tax-deepBlue mb-4">6. Disclaimer of Warranties</h2>
              <p className="mb-4">
                The Services are provided on an "as is" and "as available" basis without warranties of any kind, express 
                or implied. We do not warrant that:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li className="mb-2">The Services will be error-free or uninterrupted.</li>
                <li className="mb-2">The information provided will be accurate, complete, or suitable for your specific needs.</li>
                <li className="mb-2">Reports generated will guarantee success in any property tax appeal.</li>
              </ul>
              <p className="mb-4">You assume sole responsibility for your use of the Services.</p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-tax-deepBlue mb-4">7. Limitation of Liability</h2>
              <p className="mb-4">
                To the fullest extent permitted by law, YourWayHome.com LLC and its affiliates shall not be liable for 
                any indirect, incidental, consequential, special, or punitive damages, or for any lost profits, revenues, 
                or data, arising out of or related to your use of the Services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-tax-deepBlue mb-4">8. Indemnification</h2>
              <p className="mb-4">
                You agree to indemnify and hold harmless YourWayHome.com LLC, its affiliates, and their officers, employees, 
                and agents from any claims, liabilities, damages, losses, or expenses (including attorneys' fees) arising 
                out of or related to your use of the Services or violation of these Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-tax-deepBlue mb-4">9. Modifications</h2>
              <p className="mb-4">
                We may update these Terms from time to time. If we make material changes, we will notify you by posting 
                an updated version on the website and updating the "Effective Date" above. Your continued use of the 
                Services constitutes acceptance of the updated Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-tax-deepBlue mb-4">10. Termination</h2>
              <p className="mb-4">
                We may suspend or terminate your access to the Services at any time, for any reason, including violation 
                of these Terms. Upon termination, your right to use the Services will immediately cease.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-tax-deepBlue mb-4">11. Governing Law</h2>
              <p className="mb-4">
                These Terms are governed by the laws of the State of Colorado, without regard to conflict of law principles. 
                Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the state and 
                federal courts located in Colorado.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-tax-deepBlue mb-4">12. Contact Us</h2>
              <p className="mb-4">
                If you have any questions about these Terms, please contact us:
              </p>
              <div className="bg-tax-lightGray p-4 rounded-lg">
                <p className="mb-2"><strong>YourWayHome.com LLC</strong></p>
                <p className="mb-2">2205 W 136th Ave, Ste 106-107</p>
                <p className="mb-2">Broomfield, CO 80023</p>
                <p>Email: <a href="mailto:support@propertytaxhero.com" className="text-tax-brightBlue hover:underline">support@propertytaxhero.com</a></p>
              </div>
            </section>
          </div>
          
          <div className="mt-10 mb-6">
            <div className="flex space-x-4 justify-center">
              <Link to="/" className="text-tax-brightBlue hover:underline">Home</Link>
              <Link to="/about" className="text-tax-brightBlue hover:underline">About Us</Link>
            </div>
          </div>
          
          <div className="border-t border-tax-borderGray pt-6 mt-6 text-center text-sm text-tax-neutralGray">
            <p>&copy; {new Date().getFullYear()} PropertyTaxHero.Com. All rights reserved.</p>
            <div className="mt-2 flex justify-center space-x-4">
              <Link to="/privacy" className="hover:underline">Privacy Policy</Link>
              <span>|</span>
              <Link to="/terms" className="hover:underline">Terms of Service</Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default TermsOfService;
